import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TableColumn } from '@app/shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface MaterialItem {
  id: string;
  materialName: string;
  code: string;
  detailHtml: string;
  remark: string;
  mainImage?:
    | {
        previewUrl: string; // URL or binary data for display
        fileName: string;
        fileSize: number;
        file?: File; // Actual File object for upload
        id?: string; // For array items
      }
    | Array<{
        previewUrl: string; // URL or binary data for display
        fileName: string;
        fileSize: number;
        file?: File; // Actual File object for upload
        id?: string; // For array items
      }>;
  // API response properties for image URLs
  mainImageUrls?: string[];
  detailImageUrls?: string[];
  detailImages?: Array<{
    previewUrl: string; // URL or binary data for display
    fileName: string;
    fileSize: number;
    file?: File; // Actual File object for upload
  }>;
  // Legacy support for single detail image (for backward compatibility)
  detailImage?: {
    previewUrl: string; // URL or binary data for display
    fileName: string;
    fileSize: number;
    file?: File; // Actual File object for upload
  };
  // New properties for tracking removed images in edit mode
  removedMainImages?: string[];
  removedDetailImages?: string[];
  createdName: string;
  creationDate: string;
  status: boolean;
  // Usage-instruction video (self-hosted upload or an external YouTube/Vimeo link)
  usageVideo?: {
    previewUrl: string;
    fileName: string;
    fileSize: number;
    file?: File;
    isExisting?: boolean;
    isVideo?: boolean;
  } | null;
  usageVideoType?: UsageVideoType;
  usageVideoExternalUrl?: string;
  usageVideoExternalPlatform?: UsageVideoExternalPlatform;
  usageVideoPublishStatus?: UsageVideoPublishStatus;
  usageVideoProcessingStatus?: UsageVideoProcessingStatus;
  usageVideoThumbnailUrl?: string;
  usageVideoUrl?: string;
  removeUsageVideo?: boolean;
}

// ============================================================================
// USAGE-INSTRUCTION VIDEO
// ============================================================================

export type UsageVideoPublishStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type UsageVideoProcessingStatus =
  | 'PENDING'
  | 'COMPRESSING'
  | 'READY'
  | 'COMPRESSION_FAILED';
export type UsageVideoType = 'SELF_HOSTED' | 'EXTERNAL_LINK';
export type UsageVideoExternalPlatform = 'YOUTUBE' | 'VIMEO';

export const MATERIAL_USAGE_VIDEO_PUBLISH_STATUS_OPTIONS = [
  { label: 'material.form.options.draft', value: 'DRAFT' },
  { label: 'material.form.options.published', value: 'PUBLISHED' },
  { label: 'material.form.options.archived', value: 'ARCHIVED' },
];

export const MATERIAL_USAGE_VIDEO_TYPE_OPTIONS = [
  { label: 'material.form.options.upload_file', value: 'SELF_HOSTED' },
  { label: 'material.form.options.embed_link', value: 'EXTERNAL_LINK' },
];

// Light client-side format check only — the server does the authoritative existence check.
const YOUTUBE_URL_PATTERN =
  /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)[\w-]+|youtu\.be\/[\w-]+)/i;
const VIMEO_URL_PATTERN =
  /^https?:\/\/(www\.|player\.)?vimeo\.com\/(video\/)?\d+/i;

export function usageVideoExternalUrlValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;
  if (!value || typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();
  return YOUTUBE_URL_PATTERN.test(trimmed) || VIMEO_URL_PATTERN.test(trimmed)
    ? null
    : { invalidVideoUrl: true };
}

export interface MaterialFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  listRoute: string;
}

// ============================================================================
// FORM FIELD OPTIONS
// ============================================================================

export const MATERIAL_STATUS_OPTIONS = [
  { label: 'material.status.active', value: true },
  { label: 'material.status.inactive', value: false },
];

// ============================================================================
// FORM CONFIGURATION
// ============================================================================

export const MATERIAL_FORM_CONFIG: MaterialFormConfig = {
  title: 'material.title',
  listRoute: '/goods/material',
  formFields: [
    {
      label: 'material.form.fields.product_asset_name',
      controlName: 'materialName',
      type: 'text',
      placeholder: 'material.form.placeholders.enter_product_asset_name',
      required: true,
      section: 'material.form.sections.product_information',
      maxLength: 100,
      errorMessages: {
        required: 'material.form.validation.product_asset_name_required',
        maxlength: 'material.form.validation.product_asset_name_maxlength',
      },
    },
    {
      label: 'material.form.fields.product_asset_code',
      controlName: 'code',
      type: 'text',
      required: true,
      placeholder: 'material.form.placeholders.enter_product_asset_code',
      section: 'material.form.sections.product_information',
      maxLength: 50,
    },
    {
      label: 'material.form.fields.remark',
      controlName: 'remark',
      type: 'text',
      placeholder: 'material.form.placeholders.enter_remark',
      section: 'material.form.sections.product_information',
      maxLength: 50,
    },
    {
      label: 'material.form.fields.product_asset_description',
      controlName: 'detailHtml',
      type: 'html-description',
      placeholder: 'material.form.placeholders.enter_product_asset_description',
      section: 'material.form.sections.product_information',
      maxLength: 2000,
      colspan: 2,
      customStyles: {
        height: '150px',
        minHeight: '150px',
        maxHeight: '500px',
      },
    },
    {
      label: 'material.form.fields.main_image',
      controlName: 'mainImage',
      type: 'file',
      placeholder: 'material.form.placeholders.upload_main_image',
      section: 'material.form.sections.product_images',
      accept: '.jpg,.jpeg,.png,.gif,.webp',
      multiple: true,
      maxFiles: 1,
      maxSize: 2 * 1024 * 1024, // 2MB
      acceptedTypes: 'common.form.placeholders.accepted_material_image_types',
      required: true,
      errorMessages: {
        required: 'material.form.validation.main_image_required',
      },
      // grid-cols-6 so Video Source and Usage-Instruction Video stay on the same row as the images
      // instead of wrapping to row 2.
      sectionGridCols: 6,
      colspan: 2,
    },
    {
      label: 'material.form.fields.detail_images',
      controlName: 'detailImages',
      type: 'multi-file',
      placeholder: 'material.form.placeholders.upload_detail_images',
      section: 'material.form.sections.product_images',
      accept: '.jpg,.jpeg,.png,.gif,.webp',
      multiple: true,
      maxFiles: 36, // Gallery Images: general detail shots, optionally a full multi-angle/360 set
      maxSize: 2 * 1024 * 1024, // 2MB
      acceptedTypes: 'common.form.placeholders.accepted_gallery_image_types',
      reorderable: true,
      colspan: 2,
    },
    {
      label: 'material.form.fields.usage_video_type',
      controlName: 'usageVideoType',
      type: 'radio',
      section: 'material.form.sections.product_images',
      options: MATERIAL_USAGE_VIDEO_TYPE_OPTIONS,
      defaultValue: 'SELF_HOSTED',
      // Label and radio options share one row instead of stacking.
      labelInline: true,
      // Own cell spans both remaining columns (5-6) — the video file/link and Publish Status
      // stack beneath it as one grouped unit, independent of the taller Main Image / Gallery
      // Images cells in this row.
      colspan: 2,
    },
    {
      label: 'material.form.fields.usage_video',
      controlName: 'usageVideo',
      type: 'file',
      placeholder: 'material.form.placeholders.upload_usage_video',
      section: 'material.form.sections.product_images',
      accept: '.mp4',
      maxSize: 30 * 1024 * 1024, // 30MB (server config is authoritative)
      acceptedTypes: 'material.form.placeholders.accepted_usage_video_types',
      // Mirrors goods-service VideoPolicyProperties defaults for fast client
      // feedback; no GET endpoint exposes these yet, so kept in sync manually.
      videoMinDurationSec: 10,
      videoMaxDurationSec: 60,
      videoMaxWidthPx: 1280,
      videoMaxHeightPx: 1280,
      nestedUnder: 'usageVideoType',
      hideLabel: true,
    },
    {
      label: 'material.form.fields.usage_video_external_url',
      controlName: 'usageVideoExternalUrl',
      type: 'text',
      placeholder: 'material.form.placeholders.enter_usage_video_external_url',
      section: 'material.form.sections.product_images',
      hidden: true,
      showVideoPlatformHint: true,
      validators: [usageVideoExternalUrlValidator],
      errorMessages: {
        invalidVideoUrl: 'material.form.validation.usage_video_external_url_invalid',
      },
      // Mutually exclusive with usageVideo (mirrors its position).
      nestedUnder: 'usageVideoType',
      hideLabel: true,
    },
    {
      label: 'material.form.fields.usage_video_publish_status',
      controlName: 'usageVideoPublishStatus',
      type: 'dropdown',
      placeholder: 'material.form.placeholders.select_usage_video_publish_status',
      section: 'material.form.sections.product_images',
      options: MATERIAL_USAGE_VIDEO_PUBLISH_STATUS_OPTIONS,
      defaultValue: 'DRAFT',
      nestedUnder: 'usageVideoType',
    },
  ],
  headerActions: [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      outlined: true,
      severity: 'secondary',
    },
    { label: 'common.actions.save', action: 'save', severity: 'info' },
  ],
};

// ============================================================================
// TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const MATERIAL_DEFAULT_COLUMNS = {
  MATERIAL_NAME: 'materialName',
  CODE: 'code',
  MAIN_IMAGE: 'mainImage',
  DETAIL_HTML: 'detailHtml',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const MATERIAL_FLEXIBLE_COLUMNS = {
  REMARK: 'remark',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name
 */
export const MATERIAL_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const MATERIAL_DEFAULT_COLUMNS_ORDER: string[] = [
  MATERIAL_DEFAULT_COLUMNS.MATERIAL_NAME,
  MATERIAL_DEFAULT_COLUMNS.CODE,
  MATERIAL_DEFAULT_COLUMNS.MAIN_IMAGE,
  MATERIAL_DEFAULT_COLUMNS.DETAIL_HTML,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const MATERIAL_FLEXIBLE_COLUMNS_ORDER: string[] = [
  MATERIAL_FLEXIBLE_COLUMNS.REMARK,
  MATERIAL_FLEXIBLE_COLUMNS.CREATION_DATE,
];

export const MATERIAL_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'material.table.columns.product_asset_name',
    prop: MATERIAL_DEFAULT_COLUMNS.MATERIAL_NAME,
    width: 120,
  },
  {
    label: 'material.table.columns.product_asset_code',
    prop: MATERIAL_DEFAULT_COLUMNS.CODE,
    width: 120,
  },
  {
    label: 'material.table.columns.main_image',
    prop: MATERIAL_DEFAULT_COLUMNS.MAIN_IMAGE,
    width: 120,
    type: 'custom',
  },
  {
    label: 'material.table.columns.product_asset_description',
    prop: MATERIAL_DEFAULT_COLUMNS.DETAIL_HTML,
    width: 300,
    type: 'custom',
  },
  {
    label: 'material.table.columns.remark',
    prop: MATERIAL_FLEXIBLE_COLUMNS.REMARK,
    width: 120,
  },
  {
    label: 'material.table.columns.created_date',
    prop: MATERIAL_FLEXIBLE_COLUMNS.CREATION_DATE,
    width: 150,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'material.table.columns.actions',
    prop: MATERIAL_ACTION_COLUMN.ACTIONS,
    width: 120,
    type: 'actions',
  },
];

// ============================================================================
// SEARCH CONFIGURATION
// ============================================================================

export const MATERIAL_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'materialName',
    label: 'material.search.product_asset_name',
    type: 'text',
    placeholder: 'material.form.placeholders.enter_product_asset_name',
  },
  {
    key: 'code',
    label: 'material.search.product_asset_code',
    type: 'text',
    placeholder: 'material.form.placeholders.enter_product_asset_code',
  },
  // {
  //   key: 'status',
  //   label: 'material.search.status',
  //   type: 'select',
  //   placeholder: 'material.search.select_status',
  //   options: MATERIAL_STATUS_OPTIONS,
  // },
  // {
  //   key: 'createdDate',
  //   label: 'material.search.created_date',
  //   type: 'dateRange',
  //   range: { startKey: 'createdDateStart', endKey: 'createdDateEnd' },
  // },
];
